export type Organization = {
  organization_id: string;
  organization_name: string;
  role: "owner" | "admin" | "operator" | "viewer";
};

export type Session = {
  email: string;
  csrfToken: string;
  emailVerified: boolean;
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
  tenantAccess: boolean;
  organizations: Organization[];
};

export type Certificate = {
  id: string;
  common_name: string;
  status: string;
  renew_before_days: number;
  current_fingerprint: string | null;
  not_before: string | null;
  not_after: string | null;
  created_at: string;
  sans: string[];
};

export type Target = {
  id: string;
  agent_id: string;
  name: string;
  adapter: "nginx" | "iis";
  adapter_config: { profile?: string; serverName?: string } | null;
  enabled: boolean;
  created_at: string;
};

export type AuditEvent = {
  id: string;
  actor_user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  request_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
};

type Collection<T> = { items: T[] };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? "request_failed",
      body?.error?.message ?? "La requête au service 1SSL a échoué.",
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export type AuthResult = { csrfToken: string; emailVerified: boolean; mfaRequired: boolean; mfaSetupRequired: boolean };
export type EmailVerification = { token?: string; expiresAt?: string; delivery: string };
export type MfaSetup = { secret: string; uri: string };

export const api = {
  session: () => request<Session>("/api/me"),
  login: (email: string, password: string) => request<AuthResult>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, organizationName: string) => request<AuthResult>("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password, organizationName }) }),
  requestEmailVerification: (csrfToken: string) => request<EmailVerification>("/api/auth/email-verification/request", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: "{}" }),
  confirmEmailVerification: (csrfToken: string, token: string) => request<void>("/api/auth/email-verification/confirm", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ token }) }),
  setupMfa: (csrfToken: string) => request<MfaSetup>("/api/auth/mfa/setup", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: "{}" }),
  confirmMfaSetup: (csrfToken: string, code: string) => request<{ recoveryCodes: string[] }>("/api/auth/mfa/setup/confirm", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ code }) }),
  challengeMfa: (csrfToken: string, code: string) => request<void>("/api/auth/mfa/challenge", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify({ code }) }),
  logout: (organizationId: string, csrfToken: string) =>
    request<void>(`/api/organizations/${encodeURIComponent(organizationId)}/logout`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
    }),
  certificates: (organizationId: string) =>
    request<Collection<Certificate>>(`/api/organizations/${encodeURIComponent(organizationId)}/certificates`),
  targets: (organizationId: string) =>
    request<Collection<Target>>(`/api/organizations/${encodeURIComponent(organizationId)}/targets`),
  audit: (organizationId: string, limit = 100) =>
    request<Collection<AuditEvent>>(`/api/organizations/${encodeURIComponent(organizationId)}/audit?limit=${limit}`),
};
